import { useCallback, useEffect, useRef, useState } from "react";
import patientChatApi, { type PatientChatDeepgramTokenRequest } from "../lib/patientChat";

const DEEPGRAM_WS_URL = "wss://api.deepgram.com/v1/listen";
const RECORDING_MIME_TYPE_CANDIDATES = ["audio/webm;codecs=opus", "audio/webm"];

export interface DeepgramDictationStopOptions {
  clearStatus?: boolean;
}

interface DeepgramCloseDisposition {
  clearStatus?: boolean;
  reportConnectionError?: boolean;
}

export interface DeepgramDictationMessages {
  unsupportedBrowser?: string;
  unsupportedMimeType?: string;
  recorderFailed?: string;
  connectionFailed?: string;
  startFailed?: string;
}

export interface DeepgramTranscriptDetails {
  /** True until the session's first final transcript. */
  isSessionStart: boolean;
}

export interface UseDeepgramDictationOptions {
  purpose: PatientChatDeepgramTokenRequest["purpose"];
  sessionKey: string | number | null | undefined;
  onTranscript: (transcript: string, isFinal: boolean, details: DeepgramTranscriptDetails) => void;
  onListening?: () => void;
  onStopped?: () => void;
  onConnectionClosed?: (event: CloseEvent) => DeepgramCloseDisposition | void;
  onError?: (message: string, error?: unknown) => void;
  messages?: DeepgramDictationMessages;
  punctuate?: boolean;
  smartFormat?: boolean;
  endpointing?: number;
  logLabel?: string;
}

export interface UseDeepgramDictationResult {
  isDictating: boolean;
  isStarting: boolean;
  status: string;
  toggle: () => Promise<void>;
  stop: (options?: DeepgramDictationStopOptions) => void;
}

const getSupportedRecordingMimeType = (): string => {
  if (typeof MediaRecorder === "undefined") return "";
  if (typeof MediaRecorder.isTypeSupported !== "function") {
    return RECORDING_MIME_TYPE_CANDIDATES[0];
  }

  return (
    RECORDING_MIME_TYPE_CANDIDATES.find((mimeType) => MediaRecorder.isTypeSupported(mimeType)) || ""
  );
};

const getMicrophoneErrorMessage = (error: unknown): string => {
  const errorName =
    typeof error === "object" && error && "name" in error && typeof error.name === "string"
      ? error.name
      : "";

  if (errorName === "NotAllowedError" || errorName === "SecurityError") {
    return "Microphone access was blocked. Enable microphone permissions and try again.";
  }
  if (errorName === "NotFoundError" || errorName === "DevicesNotFoundError") {
    return "No microphone was found. Connect a microphone and try again.";
  }
  if (errorName === "NotReadableError" || errorName === "TrackStartError") {
    return "The microphone is unavailable. Close other apps using it and try again.";
  }

  return "Unable to access the microphone. Check your browser microphone settings and try again.";
};

const stopMediaStream = (stream: MediaStream | null): void => {
  stream?.getTracks().forEach((track) => track.stop());
};

export const useDeepgramDictation = ({
  purpose,
  sessionKey,
  onTranscript,
  onListening,
  onStopped,
  onConnectionClosed,
  onError,
  messages = {},
  punctuate = true,
  smartFormat = true,
  endpointing = 300,
  logLabel = "Dictation",
}: UseDeepgramDictationOptions): UseDeepgramDictationResult => {
  const [isDictating, setIsDictating] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [status, setStatus] = useState("");
  const socketRef = useRef<WebSocket | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const startNonceRef = useRef(0);
  const sessionKeyRef = useRef(sessionKey);
  const previousSessionKeyRef = useRef(sessionKey);
  const callbacksRef = useRef({
    onTranscript,
    onListening,
    onStopped,
    onConnectionClosed,
    onError,
  });
  const configurationRef = useRef({
    purpose,
    punctuate,
    smartFormat,
    endpointing,
    logLabel,
    messages,
  });

  sessionKeyRef.current = sessionKey;
  callbacksRef.current = {
    onTranscript,
    onListening,
    onStopped,
    onConnectionClosed,
    onError,
  };
  configurationRef.current = {
    purpose,
    punctuate,
    smartFormat,
    endpointing,
    logLabel,
    messages,
  };

  const stop = useCallback(({ clearStatus = false }: DeepgramDictationStopOptions = {}) => {
    startNonceRef.current += 1;

    const recorder = recorderRef.current;
    recorderRef.current = null;
    if (recorder) {
      if (recorder.state !== "inactive") recorder.stop();
      recorder.ondataavailable = null;
      recorder.onerror = null;
      recorder.onstop = null;
    }

    const socket = socketRef.current;
    socketRef.current = null;
    if (socket) {
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: "CloseStream" }));
      }
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      socket.close();
    }

    const stream = streamRef.current;
    streamRef.current = null;
    stopMediaStream(stream);

    setIsStarting(false);
    setIsDictating(false);
    setStatus(clearStatus ? "" : "Dictation stopped.");
    callbacksRef.current.onStopped?.();
  }, []);

  const toggle = useCallback(async () => {
    if (isStarting) return;
    if (isDictating) {
      stop();
      return;
    }

    const configuration = configurationRef.current;
    const callbacks = callbacksRef.current;
    const reportError = (message: string, error?: unknown) => {
      callbacks.onError?.(message, error);
    };

    if (
      typeof window === "undefined" ||
      typeof window.WebSocket === "undefined" ||
      typeof navigator === "undefined" ||
      !navigator.mediaDevices?.getUserMedia ||
      typeof MediaRecorder === "undefined"
    ) {
      reportError(
        configuration.messages.unsupportedBrowser ||
          "Live dictation is not supported in this browser."
      );
      return;
    }

    const recorderMimeType = getSupportedRecordingMimeType();
    if (!recorderMimeType) {
      reportError(
        configuration.messages.unsupportedMimeType ||
          "This browser does not support microphone dictation."
      );
      return;
    }

    const startNonce = startNonceRef.current + 1;
    startNonceRef.current = startNonce;
    const sessionKeyAtStart = sessionKeyRef.current;
    const isCurrent = () =>
      startNonceRef.current === startNonce && sessionKeyRef.current === sessionKeyAtStart;
    let stream: MediaStream | null = null;

    setIsStarting(true);
    setStatus("Starting microphone…");

    try {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
      } catch (error) {
        if (!isCurrent()) return;
        stop({ clearStatus: true });
        reportError(getMicrophoneErrorMessage(error), error);
        return;
      }

      if (!isCurrent()) {
        stopMediaStream(stream);
        return;
      }

      streamRef.current = stream;
      setStatus("Connecting dictation…");

      const tokenResponse = await patientChatApi.createDeepgramToken({
        purpose: configuration.purpose,
      });
      if (!isCurrent()) {
        if (streamRef.current === stream) streamRef.current = null;
        stopMediaStream(stream);
        return;
      }

      const queryParams = new URLSearchParams({
        model: tokenResponse.model || "nova-3",
        language: tokenResponse.language || "en-US",
        interim_results: "true",
        punctuate: String(configuration.punctuate),
        smart_format: String(configuration.smartFormat),
        endpointing: String(configuration.endpointing),
      });
      const socket = new WebSocket(`${DEEPGRAM_WS_URL}?${queryParams.toString()}`, [
        "bearer",
        tokenResponse.token,
      ]);
      socketRef.current = socket;
      let hasDeliveredFinalTranscript = false;

      socket.onopen = () => {
        if (!isCurrent()) {
          if (socketRef.current === socket) socketRef.current = null;
          socket.close();
          if (streamRef.current === stream) streamRef.current = null;
          stopMediaStream(stream);
          return;
        }

        const recorder = new MediaRecorder(stream as MediaStream, { mimeType: recorderMimeType });
        recorderRef.current = recorder;
        setIsStarting(false);
        setIsDictating(true);
        setStatus("Listening…");

        recorder.ondataavailable = async (event) => {
          if (!event.data || event.data.size === 0 || socket.readyState !== WebSocket.OPEN) return;
          try {
            socket.send(await event.data.arrayBuffer());
          } catch (error) {
            console.error(`${configuration.logLabel} audio streaming failed:`, error);
          }
        };
        recorder.onerror = (event) => {
          console.error(`${configuration.logLabel} recorder error:`, event);
          reportError(
            configuration.messages.recorderFailed || "Microphone recording failed.",
            event
          );
          stop({ clearStatus: true });
        };
        recorder.start(250);
        callbacksRef.current.onListening?.();
      };

      socket.onmessage = (event) => {
        if (!isCurrent()) return;
        try {
          const payload = JSON.parse(event.data);
          if (payload.type !== "Results") return;
          const transcript = payload.channel?.alternatives?.[0]?.transcript?.trim() || "";
          if (!transcript) return;
          const isFinal = Boolean(payload.is_final);
          callbacksRef.current.onTranscript(transcript, isFinal, {
            isSessionStart: !hasDeliveredFinalTranscript,
          });
          if (isFinal) hasDeliveredFinalTranscript = true;
        } catch (error) {
          console.error(`${configuration.logLabel} result parsing failed:`, error);
        }
      };

      socket.onerror = (event) => {
        if (isCurrent()) console.error(`${configuration.logLabel} websocket error:`, event);
      };

      socket.onclose = (event) => {
        if (!isCurrent()) return;
        setIsStarting(false);
        const disposition = callbacksRef.current.onConnectionClosed?.(event);
        if (event.code !== 1000 && disposition?.reportConnectionError !== false) {
          reportError(
            configuration.messages.connectionFailed ||
              "Dictation connection failed. Please try again.",
            event
          );
        }
        if (recorderRef.current || streamRef.current) {
          stop({ clearStatus: disposition?.clearStatus ?? event.code !== 1000 });
        }
      };
    } catch (error) {
      if (!isCurrent()) {
        if (streamRef.current === stream) streamRef.current = null;
        stopMediaStream(stream);
        return;
      }

      console.error(`${configuration.logLabel} failed to start:`, error);
      stop({ clearStatus: true });
      reportError(configuration.messages.startFailed || "Unable to start dictation.", error);
    }
  }, [isDictating, isStarting, stop]);

  useEffect(() => {
    if (Object.is(previousSessionKeyRef.current, sessionKey)) return;
    previousSessionKeyRef.current = sessionKey;
    stop({ clearStatus: true });
  }, [sessionKey, stop]);

  useEffect(() => () => stop({ clearStatus: true }), [stop]);

  return { isDictating, isStarting, status, toggle, stop };
};
